(() => {
  const normalizeConfig = (rawConfig) => {
    if (!rawConfig || !Array.isArray(rawConfig.tiers)) return null;

    const tiers = rawConfig.tiers
      .map((rawTier) => {
        const id = String(rawTier?.id || '').trim();
        const threshold = Number(rawTier?.threshold);
        const price = Number(rawTier?.price);
        const products = Array.isArray(rawTier?.products)
          ? rawTier.products
            .map((rawProduct) => {
              const productId = String(rawProduct?.id || '').trim();
              const variantId = String(rawProduct?.variantId || '').trim();
              const catalogPrice = Number(rawProduct?.catalogPrice);

              if (!productId || !variantId) return null;

              return {
                id: productId,
                variantId,
                title: String(rawProduct?.title || '').trim(),
                detail: rawProduct?.detail == null ? '' : String(rawProduct.detail).trim(),
                price,
                catalogPrice: Number.isFinite(catalogPrice) ? catalogPrice : price,
                available: rawProduct?.available !== false,
              };
            })
            .filter(Boolean)
          : [];

        if (
          !id
          || !Number.isFinite(threshold)
          || threshold <= 0
          || !Number.isFinite(price)
          || price < 0
          || products.length === 0
        ) return null;

        return {
          id,
          threshold,
          price,
          limit: 1,
          products,
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.threshold - right.threshold);

    const hasDuplicateThresholds = tiers.some((tier, index) => index > 0 && tier.threshold === tiers[index - 1].threshold);
    if (hasDuplicateThresholds) return null;

    const seenVariantIds = new Set();
    const uniqueTiers = tiers
      .map((tier) => ({
        ...tier,
        products: tier.products.filter((product) => {
          if (seenVariantIds.has(product.variantId)) return false;
          seenVariantIds.add(product.variantId);
          return true;
        }),
      }))
      .filter((tier) => tier.products.length > 0);

    const copy = rawConfig.copy && typeof rawConfig.copy === 'object'
      ? Object.fromEntries(Object.entries(rawConfig.copy).map(([key, value]) => [key, String(value || '').trim()]))
      : {};

    return uniqueTiers.length > 0 ? { tiers: uniqueTiers, copy } : null;
  };

  const normalizeCartState = (rawState) => {
    const itemCount = Number(rawState?.itemCount);
    const subtotal = Number(rawState?.subtotal);
    const cartVariantIds = Array.isArray(rawState?.cartVariantIds)
      ? rawState.cartVariantIds.map(String).filter(Boolean)
      : [];
    const selectedProductIds = Array.isArray(rawState?.selectedProductIds)
      ? rawState.selectedProductIds.map(String).filter(Boolean)
      : [];

    return {
      itemCount: Number.isFinite(itemCount) && itemCount >= 0 ? itemCount : 0,
      subtotal: Number.isFinite(subtotal) && subtotal >= 0 ? subtotal : 0,
      cartVariantIds: new Set(cartVariantIds),
      selectedProductIds: new Set(selectedProductIds),
    };
  };

  const getTierOccupancy = (tier, state, isUnlocked = true) => {
    if (!isUnlocked || !tier || !state) return null;

    const addonProduct = tier.products.find((product) => state.selectedProductIds.has(product.id));
    if (addonProduct) return { productId: addonProduct.id, source: 'addon' };

    const cartProduct = tier.products.find((product) => state.cartVariantIds.has(product.variantId));
    return cartProduct ? { productId: cartProduct.id, source: 'cart' } : null;
  };

  const getMilestonePresentation = (tier, state, isUnlocked) => {
    const occupancy = getTierOccupancy(tier, state, isUnlocked);

    if (!isUnlocked) {
      return { iconKey: 'lock', isOccupied: false, showBadge: false, statusKey: 'statusLocked' };
    }

    if (occupancy) {
      return {
        iconKey: 'check',
        isOccupied: true,
        showBadge: false,
        statusKey: occupancy.source === 'cart' ? 'statusInCart' : 'statusAdded',
      };
    }

    return { iconKey: 'gift', isOccupied: false, showBadge: true, statusKey: 'statusAvailable' };
  };

  const calculateTrackProgress = (tiers, subtotal) => {
    if (!Array.isArray(tiers) || tiers.length === 0) return 0;
    if (tiers.length === 1) return subtotal >= tiers[0].threshold ? 1 : 0;

    const firstThreshold = tiers[0].threshold;
    const lastThreshold = tiers.at(-1).threshold;

    if (subtotal <= firstThreshold) return 0;
    if (subtotal >= lastThreshold) return 1;

    const segmentIndex = tiers.findIndex((tier) => tier.threshold > subtotal) - 1;
    const lowerThreshold = tiers[segmentIndex].threshold;
    const upperThreshold = tiers[segmentIndex + 1].threshold;
    const segmentProgress = (subtotal - lowerThreshold) / (upperThreshold - lowerThreshold);

    return (segmentIndex + segmentProgress) / (tiers.length - 1);
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      calculateTrackProgress,
      getMilestonePresentation,
      getTierOccupancy,
      normalizeCartState,
      normalizeConfig,
    };
    return;
  }

  const ELEMENT_NAME = 'threshold-addon-offers';
  if (customElements.get(ELEMENT_NAME)) return;

  let preferredThreshold = null;

  const ICONS = Object.freeze({
    check: '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m4.5 10 3.5 3.5 7.5-7.5"/></svg>',
    cart: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39A2 2 0 0 0 9.68 16h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>',
    gift: '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 8h12v8H4zM3 6h14v2H3zM10 6v10"/><path d="M10 6H7.5A2 2 0 1 1 9.5 4v2Zm0 0h2.5a2 2 0 1 0-2-2v2Z"/></svg>',
    lock: '<svg viewBox="0 0 20 20" aria-hidden="true"><rect x="5" y="8" width="10" height="8" rx="2"/><path d="M7.5 8V6.5a2.5 2.5 0 0 1 5 0V8"/></svg>',
  });

  const formatMoney = (cents) => '$' + (cents / 100).toFixed(2);
  const formatCompactMoney = (cents) => '$' + Number((cents / 100).toFixed(2));

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const interpolate = (template, values = {}) => Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll('%%' + key + '%%', String(value)),
    String(template || ''),
  );

  const decodeHtmlCopy = (copy) => {
    const textarea = document.createElement('textarea');
    return Object.fromEntries(Object.entries(copy).map(([key, value]) => {
      textarea.innerHTML = String(value || '');
      return [key, textarea.value];
    }));
  };

  class ThresholdAddonOffers extends HTMLElement {
    constructor() {
      super();
      this.handleCartUpdated = this.handleCartUpdated.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleProductAjaxError = this.handleProductAjaxError.bind(this);
      this.handleProductsKeydown = this.handleProductsKeydown.bind(this);
      this.scheduleSliderUpdate = this.scheduleSliderUpdate.bind(this);
    }

    connectedCallback() {
      if (this.isConnectedToController) return;

      if (this.hasAttribute('data-placeholder')) {
        document.addEventListener('cart:updated', this.handleCartUpdated);
        this.isConnectedToController = true;
        this.setLayerVisibility(false);
        return;
      }

      this.config = this.readConfig();
      if (!this.config) {
        this.setLayerVisibility(false);
        this.hidden = true;
        return;
      }

      this.addEventListener('click', this.handleClick);
      this.addEventListener('submit', this.handleFormSubmit, true);
      document.addEventListener('cart:updated', this.handleCartUpdated);
      document.addEventListener('product-ajax:error', this.handleProductAjaxError);
      this.isConnectedToController = true;

      this.copy = this.readCopy();
      this.state = this.readCartState();
      const preferredTier = this.config.tiers.find((tier) => tier.threshold === preferredThreshold);
      this.activeThreshold = preferredTier && this.isTierUnlocked(preferredTier)
        ? preferredTier.threshold
        : this.closestUnlockedTier().threshold;
      preferredThreshold = this.activeThreshold;
      this.cacheElements();
      this.initializeDisclosureState();
      this.initializeSlider();
      this.buildMilestones();
      this.hidden = false;
      this.setLayerVisibility(this.state.itemCount > 0);
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('submit', this.handleFormSubmit, true);
      document.removeEventListener('cart:updated', this.handleCartUpdated);
      document.removeEventListener('product-ajax:error', this.handleProductAjaxError);
      if (this.cartUpdateFrame) cancelAnimationFrame(this.cartUpdateFrame);
      if (this.sliderUpdateFrame) cancelAnimationFrame(this.sliderUpdateFrame);
      this.cartUpdateFrame = null;
      this.sliderUpdateFrame = null;
      this.elements?.products?.removeEventListener('scroll', this.scheduleSliderUpdate);
      this.elements?.products?.removeEventListener('keydown', this.handleProductsKeydown);
      this.sliderResizeObserver?.disconnect();
      this.sliderResizeObserver = null;
      this.clearAddPending(false);
      this.isConnectedToController = false;
    }

    readConfig() {
      try {
        const rawConfig = JSON.parse(this.querySelector('[data-addon-config]')?.textContent || '');
        return normalizeConfig(rawConfig);
      } catch (error) {
        console.error('[Threshold add-ons] Invalid configuration.', error);
        return null;
      }
    }

    readCartState() {
      try {
        const stateElement = document.getElementById(this.dataset.stateId);
        return normalizeCartState(JSON.parse(stateElement?.textContent || '{}'));
      } catch (error) {
        console.error('[Threshold add-ons] Invalid cart state.', error);
        return normalizeCartState(null);
      }
    }

    readCopy() {
      const localizedCopy = decodeHtmlCopy(this.config.copy);

      return {
        ...localizedCopy,
        activeLabel: this.dataset.activeLabel?.trim() || localizedCopy.activeLabel,
        savingsLabel: this.dataset.savingsLabel?.trim() || localizedCopy.savingsLabel,
      };
    }

    cacheElements() {
      this.elements = {
        body: this.querySelector('[data-addon-body]'),
        disclosure: this.querySelector('[data-addon-disclosure]'),
        milestones: this.querySelector('[data-addon-milestones]'),
        track: this.querySelector('[data-addon-track]'),
        tierTitle: this.querySelector('[data-addon-tier-title]'),
        tierBadge: this.querySelector('[data-addon-tier-badge]'),
        products: this.querySelector('[data-addon-products]'),
        sliderControls: this.querySelector('[data-addon-slider-controls]'),
        sliderProgress: this.querySelector('[data-addon-slider-progress]'),
        sliderPrevious: this.querySelector('[data-addon-scroll-direction="-1"]'),
        sliderNext: this.querySelector('[data-addon-scroll-direction="1"]'),
        next: this.querySelector('[data-addon-next]'),
        announcement: this.querySelector('[data-addon-announcement]'),
        promoLead: this.querySelector('[data-addon-promo-lead]'),
        promoLabel: this.querySelector('[data-addon-promo-label]'),
        promoMeta: this.querySelector('[data-addon-promo-meta]'),
        cartIcon: this.querySelector('[data-addon-cart-icon]')?.innerHTML.trim() || ICONS.cart,
        productTemplates: new Map(
          [...this.querySelectorAll('[data-addon-product-template]')]
            .map((template) => [template.dataset.addonProductTemplate, template]),
        ),
      };
    }

    initializeSlider() {
      this.elements.products.addEventListener('scroll', this.scheduleSliderUpdate, { passive: true });
      this.elements.products.addEventListener('keydown', this.handleProductsKeydown);

      if ('ResizeObserver' in window) {
        this.sliderResizeObserver = new ResizeObserver(this.scheduleSliderUpdate);
        this.sliderResizeObserver.observe(this.elements.products);
      }
    }

    initializeDisclosureState() {
      const isCollapsed = this.dataset.context === 'drawer';

      this.classList.toggle('is-collapsed', isCollapsed);
      this.elements.disclosure.setAttribute('aria-expanded', String(!isCollapsed));
      this.elements.disclosure.setAttribute('aria-label', isCollapsed ? this.copy.expand : this.copy.collapse);
      this.elements.body.hidden = false;
      this.elements.body.setAttribute('aria-hidden', String(isCollapsed));
      this.elements.body.inert = isCollapsed;
    }

    buildMilestones() {
      const fragment = document.createDocumentFragment();
      const tierCount = this.config.tiers.length;

      this.style.setProperty('--addon-tier-count', String(tierCount));
      this.style.setProperty('--addon-track-inset', 'calc(' + (50 / tierCount) + '% + 9px)');
      this.classList.toggle('threshold-addon--single-tier', tierCount === 1);

      this.config.tiers.forEach((tier) => {
        const item = createElement('li', 'threshold-addon__milestone');
        const button = createElement('button', 'threshold-addon__milestone-button');
        const node = createElement('span', 'threshold-addon__milestone-node');
        const badge = createElement('span', 'threshold-addon__milestone-badge', this.copy.unlocked);
        const amount = createElement('span', 'threshold-addon__milestone-amount', '$' + (tier.threshold / 100));

        button.type = 'button';
        button.dataset.tierThreshold = String(tier.threshold);
        badge.hidden = true;
        badge.dataset.milestoneBadge = '';
        node.dataset.milestoneIcon = '';
        button.append(badge, node, amount);
        item.append(button);
        fragment.append(item);
      });

      this.elements.milestones.replaceChildren(fragment);
    }

    handleClick(event) {
      const sliderButton = event.target.closest('[data-addon-scroll-direction]');
      if (sliderButton) {
        this.scrollProducts(Number(sliderButton.dataset.addonScrollDirection));
        return;
      }

      const disclosure = event.target.closest('[data-addon-disclosure]');
      if (disclosure) {
        this.toggleDisclosure(disclosure);
        return;
      }

      const milestone = event.target.closest('[data-tier-threshold]');
      if (milestone) {
        this.activeThreshold = Number(milestone.dataset.tierThreshold);
        preferredThreshold = this.activeThreshold;
        this.render();
      }
    }

    handleProductsKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.scrollProducts(event.key === 'ArrowRight' ? 1 : -1);
    }

    scheduleSliderUpdate() {
      if (this.sliderUpdateFrame) return;
      this.sliderUpdateFrame = requestAnimationFrame(() => {
        this.sliderUpdateFrame = null;
        this.updateSliderControls();
      });
    }

    updateSliderControls() {
      const { products, sliderControls, sliderProgress, sliderPrevious, sliderNext } = this.elements;
      if (!products || !sliderControls || !sliderProgress || !sliderPrevious || !sliderNext) return;

      const maxScroll = Math.max(0, products.scrollWidth - products.clientWidth);
      const scrollPosition = Math.min(maxScroll, Math.max(0, products.scrollLeft));
      const isScrollable = maxScroll > 2;
      const visibleRatio = products.scrollWidth > 0 ? Math.min(1, products.clientWidth / products.scrollWidth) : 1;
      const progress = isScrollable
        ? visibleRatio + (1 - visibleRatio) * (scrollPosition / maxScroll)
        : 1;

      sliderControls.hidden = !isScrollable;
      sliderPrevious.disabled = !isScrollable || scrollPosition <= 2;
      sliderNext.disabled = !isScrollable || scrollPosition >= maxScroll - 2;
      sliderProgress.style.setProperty('--addon-slider-progress', String(progress));
    }

    scrollProducts(direction) {
      if (!Number.isFinite(direction) || direction === 0) return;

      const { products } = this.elements;
      const firstCard = products.querySelector('.threshold-addon__product');
      const styles = window.getComputedStyle(products);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const cardStep = firstCard ? firstCard.getBoundingClientRect().width + gap : products.clientWidth * 0.75;
      const visibleCards = Math.max(1, Math.floor(products.clientWidth / cardStep) - 1);
      const maxScroll = Math.max(0, products.scrollWidth - products.clientWidth);
      const target = Math.min(maxScroll, Math.max(0, products.scrollLeft + direction * cardStep * visibleCards));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      products.scrollTo({ left: target, behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    handleFormSubmit(event) {
      const form = event.target.closest('[data-addon-product-form]');
      if (!form || !this.contains(form)) return;

      if (this.isAddPending) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      this.isAddPending = true;
      const submitButton = event.submitter || form.querySelector('[data-addon-submit]');
      this.querySelectorAll('[data-addon-submit]').forEach((button) => {
        if (button !== submitButton) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        }
      });

      clearTimeout(this.addPendingTimer);
      this.addPendingTimer = window.setTimeout(() => {
        this.clearAddPending();
      }, 30000);
    }

    handleProductAjaxError() {
      if (this.isAddPending) this.clearAddPending();
    }

    clearAddPending(shouldRender = true) {
      clearTimeout(this.addPendingTimer);
      this.addPendingTimer = null;
      const wasPending = this.isAddPending;
      this.isAddPending = false;

      if (!wasPending || !shouldRender || !this.config || !this.state || this.state.itemCount === 0) return;
      this.render();
    }

    handleCartUpdated(event) {
      this.latestCart = event.detail?.cart;
      if (this.cartUpdateFrame) return;

      this.cartUpdateFrame = requestAnimationFrame(() => {
        this.cartUpdateFrame = null;
        if (!this.isConnected) return;
        this.clearAddPending(false);

        if (this.hasAttribute('data-placeholder')) {
          this.hydrateFromSections(this.latestCart);
          return;
        }

        const previousSelections = this.state.selectedProductIds;
        this.state = this.readCartState();
        this.setLayerVisibility(this.state.itemCount > 0);

        if (this.state.itemCount === 0) return;

        this.render();
        const addedSelection = [...this.state.selectedProductIds]
          .find((productId) => !previousSelections.has(productId));
        if (addedSelection) {
          const product = this.config.tiers
            .flatMap((tier) => tier.products)
            .find((item) => item.id === addedSelection);
          if (product) {
            this.elements.announcement.textContent = interpolate(this.copy.addedAnnouncement, {
              title: product.title,
            });
          }
        }
      });
    }

    hydrateFromSections(cart) {
      if (!cart || cart.item_count <= 0) return;

      const sectionMarkup = cart.sections?.[this.dataset.sectionId];
      if (typeof sectionMarkup !== 'string' || sectionMarkup.length === 0) return;

      const parsedDocument = new DOMParser().parseFromString(sectionMarkup, 'text/html');
      const selector = ELEMENT_NAME + '[data-context="' + this.dataset.context + '"]:not([data-placeholder])';
      const replacement = parsedDocument.querySelector(selector);
      if (!replacement) return;

      this.replaceWith(replacement);
    }

    setLayerVisibility(isVisible) {
      const layer = this.closest('.cart-drawer__offer-layer');
      if (layer) {
        layer.hidden = !isVisible;
      } else {
        this.hidden = !isVisible;
      }
    }

    toggleDisclosure(button) {
      const isCollapsed = this.classList.toggle('is-collapsed');
      button.setAttribute('aria-expanded', String(!isCollapsed));
      button.setAttribute('aria-label', isCollapsed ? this.copy.expand : this.copy.collapse);

      this.elements.body.hidden = false;
      this.elements.body.setAttribute('aria-hidden', String(isCollapsed));
      this.elements.body.inert = isCollapsed;
    }

    closestUnlockedTier() {
      return this.config.tiers.filter((tier) => this.isTierUnlocked(tier)).at(-1) || this.config.tiers[0];
    }

    currentTier() {
      return this.config.tiers.find((tier) => tier.threshold === this.activeThreshold) || this.config.tiers[0];
    }

    isTierUnlocked(tier) {
      return this.state.subtotal >= tier.threshold;
    }

    getMaximumSaving(products) {
      return Math.max(0, ...products.map((product) => product.catalogPrice - product.price));
    }

    render() {
      const unlockedCount = this.config.tiers.filter((tier) => this.isTierUnlocked(tier)).length;
      this.renderPromotionSummary(unlockedCount);
      this.renderMilestones();
      this.renderProducts(this.currentTier());
      this.renderNextTier();
    }

    renderPromotionSummary(unlockedCount = this.config.tiers.filter((tier) => this.isTierUnlocked(tier)).length) {
      if (!this.elements.promoLead || !this.elements.promoLabel || !this.elements.promoMeta) return;

      const unlockedTiers = this.config.tiers.filter((tier) => this.isTierUnlocked(tier));
      if (unlockedTiers.length === 0) {
        const firstTier = this.config.tiers[0];
        const lowestPrice = Math.min(...firstTier.products.map((product) => product.price));
        this.elements.promoLead.textContent = interpolate(this.copy.lockedLead, {
          amount: formatMoney(firstTier.threshold - this.state.subtotal),
        });
        this.elements.promoLabel.textContent = this.copy.lockedLabel;
        this.elements.promoMeta.textContent = interpolate(this.copy.lockedMeta, {
          threshold: formatCompactMoney(firstTier.threshold),
          price: formatMoney(lowestPrice),
        });
        return;
      }

      const unlockedProducts = unlockedTiers.flatMap((tier) => tier.products);
      const maxSaving = this.getMaximumSaving(unlockedProducts);
      const occupiedCount = unlockedTiers.filter((tier) => getTierOccupancy(tier, this.state)).length;
      const savingText = maxSaving > 0
        ? ' · ' + this.copy.savingsLabel + ' ' + formatCompactMoney(maxSaving)
        : ' · ' + this.copy.tierLimit;

      this.elements.promoLead.textContent = '';
      this.elements.promoLabel.textContent = this.copy.activeLabel;
      this.elements.promoMeta.textContent = occupiedCount > 0
        ? interpolate(this.copy.selectedSummary, {
          occupied: occupiedCount,
          unlocked: unlockedCount,
          total: this.config.tiers.length,
        })
        : interpolate(this.copy.unlockedSummary, {
          unlocked: unlockedCount,
          total: this.config.tiers.length,
          saving: savingText,
        });
    }

    renderMilestones() {
      const buttons = [...this.elements.milestones.querySelectorAll('[data-tier-threshold]')];
      this.elements.track.style.setProperty('--addon-track-scale', String(calculateTrackProgress(
        this.config.tiers,
        this.state.subtotal,
      )));

      buttons.forEach((button, index) => {
        const tier = this.config.tiers[index];
        const isUnlocked = this.isTierUnlocked(tier);
        const isActive = tier.threshold === this.activeThreshold;
        const presentation = getMilestonePresentation(tier, this.state, isUnlocked);
        const icon = button.querySelector('[data-milestone-icon]');
        const badge = button.querySelector('[data-milestone-badge]');

        button.classList.toggle('is-unlocked', isUnlocked);
        button.classList.toggle('is-occupied', presentation.isOccupied);
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
        button.setAttribute('aria-label', interpolate(this.copy.milestoneLabel, {
          threshold: formatCompactMoney(tier.threshold),
          status: this.copy[presentation.statusKey],
        }) + (isActive ? this.copy.currentSuffix : ''));
        badge.hidden = !presentation.showBadge;
        icon.innerHTML = ICONS[presentation.iconKey];
      });
    }

    renderProducts(tier) {
      const isUnlocked = this.isTierUnlocked(tier);
      const tierOccupancy = getTierOccupancy(tier, this.state, isUnlocked);

      this.elements.tierTitle.textContent = tier.products.length > 1 ? this.copy.chooseOne : this.copy.tierProducts;
      this.elements.tierBadge.hidden = !isUnlocked;
      this.elements.tierBadge.textContent = isUnlocked ? this.copy.unlocked : '';

      const fragment = document.createDocumentFragment();
      tier.products.forEach((product) => {
        const isSelected = isUnlocked && this.state.selectedProductIds.has(product.id);
        const isExistingInCart = isUnlocked && !isSelected && this.state.cartVariantIds.has(product.variantId);
        const isDisabledByPeer = tier.limit === 1
          && Boolean(tierOccupancy)
          && tierOccupancy.productId !== product.id
          && !isExistingInCart;
        const isDisabled = this.isAddPending
          || !isUnlocked
          || !product.available
          || isSelected
          || isExistingInCart
          || isDisabledByPeer;
        const card = this.createProductCard(product, {
          isSelected,
          isExistingInCart,
          isDisabledByPeer,
          isDisabled,
          isPending: this.isAddPending,
        });
        if (card) fragment.append(card);
      });
      this.elements.products.replaceChildren(fragment);
      this.elements.products.scrollLeft = 0;
      this.scheduleSliderUpdate();
    }

    createProductCard(product, state) {
      const template = this.elements.productTemplates.get(product.id);
      const card = template?.content.querySelector('.threshold-addon__product')?.cloneNode(true);
      const form = card?.querySelector('[data-addon-product-form]');
      if (!card || !form) return null;

      customElements.upgrade(form);

      const button = form.querySelector('[data-addon-submit]');
      const buttonIcon = button?.querySelector('[data-addon-button-icon]');
      const status = card.querySelector('[data-addon-product-status]');
      if (!button || !buttonIcon || !status) return null;

      card.classList.add('threshold-addon__product');
      card.classList.toggle('is-selected', state.isSelected);
      card.classList.toggle('is-cart-existing', state.isExistingInCart);
      card.classList.toggle('is-peer-disabled', state.isDisabledByPeer);

      button.disabled = state.isDisabled;
      button.classList.toggle('is-disabled', state.isDisabled);
      button.setAttribute('aria-disabled', String(state.isDisabled));
      button.setAttribute('aria-label', this.getAddButtonLabel(product, state));
      buttonIcon.innerHTML = state.isSelected || state.isExistingInCart
        ? ICONS.check
        : state.isDisabled
          ? ICONS.lock
          : this.elements.cartIcon;

      status.hidden = !state.isSelected && !state.isExistingInCart;
      status.textContent = state.isSelected ? this.copy.statusAdded : this.copy.statusInCart;
      return card;
    }

    getAddButtonLabel(product, state) {
      if (state.isSelected) return interpolate(this.copy.buttonAdded, { title: product.title });
      if (state.isExistingInCart) return interpolate(this.copy.buttonInCart, { title: product.title });
      if (state.isPending) return interpolate(this.copy.buttonAdding, { title: product.title });
      if (!product.available) return interpolate(this.copy.buttonSoldOut, { title: product.title });
      if (state.isDisabledByPeer) return interpolate(this.copy.buttonPeerDisabled, { title: product.title });
      if (!this.isTierUnlocked(this.currentTier())) return interpolate(this.copy.buttonLocked, { title: product.title });
      return interpolate(this.copy.buttonAdd, { title: product.title, price: formatMoney(product.price) });
    }

    renderNextTier() {
      const nextTier = this.config.tiers.find((tier) => !this.isTierUnlocked(tier));
      if (!nextTier) {
        this.elements.next.textContent = this.copy.allUnlocked;
        return;
      }

      const amountToken = '%%amount%%';
      const nextText = interpolate(this.copy.nextTier, {
        amount: amountToken,
        threshold: formatCompactMoney(nextTier.threshold),
      });
      const [beforeAmount, afterAmount = ''] = nextText.split(amountToken);
      this.elements.next.replaceChildren(
        document.createTextNode(beforeAmount),
        createElement('strong', '', formatMoney(nextTier.threshold - this.state.subtotal)),
        document.createTextNode(afterAmount),
      );
    }
  }

  customElements.define(ELEMENT_NAME, ThresholdAddonOffers);
})();
